<template>
  <div
    v-for="(route, routeIndex) in travel.routes"
    :key="routeIndex"
    class="journey-list__departures"
  >
    <details
      v-if="!isMobile || isRebooking"
      :ref="registerAccordion"
    >
      <summary
        class="journey-list__overview"
        @click="openDetailsDrawer(travelIndex, routeIndex)"
      >
        <JourneyListTravel
          :routeCodes="route.route_codes || []"
          :departureTimestamp="
            route.legs[0].departure_station.departure_timestamp || ''
          "
          :arrivalTimestamp="
            route.legs[route.legs.length - 1].arrival_station
              .arrival_timestamp || ''
          "
          :travelDuration="route.travel_duration || ''"
          :bundles="route.bundles || []"
          :legs="route.legs || []"
        />
      </summary>
      <div class="journey-list__details fifty-fifty-grid">
        <section>
          <JourneyListDetails
            :legs="getValidLegs(route.legs)"
            :travelDate="route.legs[0].service_schedule_date.split('T')[0]"
            :serviceName="route.legs[0].service_name"
            :departureStation="route.legs[0].departure_station.name"
            :arrivalStation="
              route.legs[route.legs.length - 1].arrival_station.name
            "
            @update-route-codes="
              updateRouteCodes(travelIndex, routeIndex, $event)
            "
          />
        </section>
        <JourneyListTickets
          :bundles="route.bundles || []"
          :travelIndex="travelIndex"
          :routeIndex="routeIndex"
          :selectedBundle="selectedBundle"
          :showOutbound="showOutbound"
          :legs="route.legs || []"
          @bundle-selected="handleBundleSelection"
        />
      </div>
    </details>
    <div
      v-else
      class="journey-list__overview"
      @click="openDetailsDrawer(travelIndex, routeIndex)"
    >
      <JourneyListTravel
        :key="`mobile-${travelIndex}-${routeIndex}`"
        :routeCodes="routeCodes"
        :departureTimestamp="
          route.legs[0].departure_station.departure_timestamp || ''
        "
        :arrivalTimestamp="
          route.legs[route.legs.length - 1].arrival_station.arrival_timestamp ||
          ''
        "
        :travelDuration="route.travel_duration || ''"
        :bundles="route.bundles || []"
        :legs="route.legs || []"
      />
    </div>
  </div>
</template>

<script lang="ts">
  import {
    defineComponent,
    PropType,
    watch,
    nextTick,
    ref,
    onMounted,
  } from 'vue'
  import { Travel, Route } from '@/types/travelTypes'
  import { SearchSelection } from '@/types/searchTypes'
  import JourneyListTravel from './JourneyListTravel.vue'
  import JourneyListDetails from './JourneyListDetails.vue'
  import JourneyListTickets from './JourneyListTickets.vue'
  import { useBreakpoints } from '@/composables/useBreakpoints'
  import { searchServices } from '@/services/journeyService'
  import { useJourneyStore } from '@/stores/journeyStore'

  export default defineComponent({
    name: 'JourneySelectionHandler',
    components: {
      JourneyListTravel,
      JourneyListDetails,
      JourneyListTickets,
    },
    props: {
      travel: {
        type: Object as PropType<Travel>,
        required: true,
      },
      travelIndex: {
        type: Number,
        required: true,
      },
      selectedBundle: {
        type: Object as PropType<SearchSelection | null>,
        default: null,
      },
      showOutbound: {
        type: Boolean,
        required: true,
      },
      isMobile: {
        type: Boolean,
        default: false,
      },
      isRebooking: {
        type: Boolean,
        default: false,
      },
    },
    emits: [
      'update-route-codes',
      'bundle-selected',
      'open-details-drawer',
      'open-details',
    ],
    setup(props, { emit }) {
      const { isMobile } = useBreakpoints()
      const isUpdating = ref(false)
      const routeCodes = ref<string[]>([])
      const journeyStore = useJourneyStore()
      const accordionRefs = ref<Set<HTMLDetailsElement>>(new Set())

      const registerAccordion = (el: HTMLDetailsElement | null) => {
        if (el) {
          accordionRefs.value.add(el)
        }
      }

      const closeAccordions = () => {
        accordionRefs.value.forEach((accordion) => {
          if (accordion) {
            accordion.open = false
          }
        })
      }

      watch(
        () => props.travel,
        () => {
          accordionRefs.value.clear()
        }
      )

      watch(
        () => journeyStore.journeySearchResults,
        () => {
          nextTick(() => {
            closeAccordions()
          })
        }
      )

      const getValidLegs = (legs: any[]) => {
        return legs.filter((leg) => leg.service_name)
      }

      const openDetailsDrawer = (travelIndex: number, routeIndex: number) => {
        emit('open-details-drawer', { travelIndex, routeIndex })
      }

      const handleBundleSelection = (selection: any) => {
        emit('bundle-selected', selection)
      }

      const updateRouteCodes = (
        travelIndex: number,
        routeIndex: number,
        codes: string[]
      ) => {
        if (!isUpdating.value) {
          routeCodes.value = codes
          emit('update-route-codes', {
            travelIndex,
            routeIndex,
            routeCodes: codes,
          })
        }
      }

      const fetchRouteCodesForMobile = async () => {
        if (!isMobile.value || !props.travel.routes?.[0]?.legs?.length) return

        try {
          isUpdating.value = true
          const leg = props.travel.routes[0].legs[0]
          const data = await searchServices(
            leg.service_name,
            leg.service_schedule_date.split('T')[0]
          )

          if (data?.data?.services?.[0]?.meta_data) {
            const codes = data.data.services[0].meta_data
              .filter((meta: any) => meta.key === 'route_code')
              .map((meta: any) => meta.value)

            if (codes.length > 0) {
              routeCodes.value = codes
              updateRouteCodes(props.travelIndex, 0, codes)
            }
          }
        } catch (error) {
          console.error('Error fetching route codes:', error)
        } finally {
          isUpdating.value = false
        }
      }

      onMounted(() => {
        if (props.travel.routes?.[0]?.route_codes) {
          routeCodes.value = props.travel.routes[0].route_codes
        }
        if (isMobile.value) {
          fetchRouteCodesForMobile()
        }
      })

      watch(
        () => props.travel.routes,
        (newRoutes) => {
          if (!isUpdating.value && newRoutes?.[0]?.route_codes) {
            isUpdating.value = true
            routeCodes.value = newRoutes[0].route_codes
            nextTick(() => {
              updateRouteCodes(props.travelIndex, 0, routeCodes.value)
              isUpdating.value = false
            })
          }
        },
        { deep: true }
      )

      return {
        isMobile,
        routeCodes,
        getValidLegs,
        openDetailsDrawer,
        handleBundleSelection,
        updateRouteCodes,
        registerAccordion,
      }
    },
  })
</script>

<style lang="scss" scoped>
  .journey-list {
    &__departures {
      color: $clr-primary;
      margin-bottom: 1rem;
      border-radius: $border-radius;
      background-color: $clr-white;
      border: 1px solid $clr-primary-100;
      box-shadow: 0px 0px 4px 1px #2a347a26;
      display: flex;
      flex-direction: column;
    }

    &__overview {
      display: flex;
      flex-direction: row;
      cursor: pointer;
    }

    &__details {
      padding: 0 1rem;
      border-top: 1px solid $clr-primary-100;
    }
  }
</style>
